import { lazy, Suspense, useState, useRef } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster as ShadcnToaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import SplashScreen from "@/components/SplashScreen";
import { TransitionVariant } from "@/lib/pageTransitionVariants";
import { useDisableSwipeBack } from "@/hooks/useDisableSwipeBack";
import { DesktopGate } from "@/components/DesktopGate";

// Lazy load all pages
const Home = lazy(() => import("./pages/Home"));
const Catalogo = lazy(() => import("./pages/Catalogo"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Card = lazy(() => import("./pages/Card"));
const Ricompense = lazy(() => import("./pages/Ricompense"));
const RewardDetail = lazy(() => import("./pages/RewardDetail"));
const MieiRiscatti = lazy(() => import("./pages/MieiRiscatti"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminGestioneAdmin = lazy(() => import("./pages/AdminGestioneAdmin"));
const AdminCaricaPunti = lazy(() => import("./pages/AdminCaricaPunti"));
const AdminScaglioneDetail = lazy(() => import("./pages/AdminScaglioneDetail"));
const AdminMovimentoDetail = lazy(() => import("./pages/AdminMovimentoDetail"));
const AdminNuovoProdotto = lazy(() => import("./pages/AdminNuovoProdotto"));
const AdminRedemptionDetail = lazy(() => import("./pages/AdminRedemptionDetail"));
const AdminCategoryDetail = lazy(() => import("./pages/AdminCategoryDetail"));
const AdminPromoDetail = lazy(() => import("./pages/AdminPromoDetail"));
const AdminPromoArchivio = lazy(() => import("./pages/AdminPromoArchivio"));
const AdminRewardDetail = lazy(() => import("./pages/AdminRewardDetail"));
const Promo = lazy(() => import("./pages/Promo"));
const Notifiche = lazy(() => import("./pages/Notifiche"));
const NotFound = lazy(() => import("./pages/NotFound"));
const InfoNegozio = lazy(() => import("./pages/InfoNegozio"));
const TerminiCondizioni = lazy(() => import("./pages/TerminiCondizioni"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Profilo = lazy(() => import("./pages/Profilo"));
const Concierge = lazy(() => import("./pages/Concierge"));
const NativeCallback = lazy(() => import("./pages/NativeCallback"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
    },
  },
});

// ─── Depth map ────────────────────────────────────────────────────────────────
const DEPTH_MAP: Array<{ pattern: RegExp; depth: number }> = [
  { pattern: /^\/$/, depth: 0 },
  { pattern: /^\/login/, depth: 0 },
  { pattern: /^\/register/, depth: 0 },
  { pattern: /^\/forgot-password/, depth: 0 },
  { pattern: /^\/reset-password/, depth: 0 },
  { pattern: /^\/catalogo$/, depth: 1 },
  { pattern: /^\/catalogo\/categoria\/[^/]+$/, depth: 2 },
  { pattern: /^\/catalogo\/categoria\/[^/]+\/[^/]+/, depth: 3 },
  { pattern: /^\/catalogo\/[^/]+$/, depth: 2 },
  { pattern: /^\/promo/, depth: 1 },
  { pattern: /^\/wishlist/, depth: 1 },
  { pattern: /^\/card/, depth: 1 },
  { pattern: /^\/profilo/, depth: 1 },
  { pattern: /^\/concierge/, depth: 1 },
  { pattern: /^\/ricompense$/, depth: 1 },
  { pattern: /^\/ricompense\//, depth: 2 },
  { pattern: /^\/miei-riscatti/, depth: 2 },
  { pattern: /^\/notifiche/, depth: 2 },
  { pattern: /^\/info-negozio/, depth: 2 },
  { pattern: /^\/termini-condizioni/, depth: 2 },
  { pattern: /^\/privacy-policy/, depth: 2 },
  { pattern: /^\/cookie-policy/, depth: 2 },
  { pattern: /^\/admin$/, depth: 3 },
  { pattern: /^\/admin\//, depth: 4 },
];

function getDepth(pathname: string): number {
  for (const { pattern, depth } of DEPTH_MAP) {
    if (pattern.test(pathname)) return depth;
  }
  return 1;
}

function getTransitionVariant(current: string, previous: string | null): TransitionVariant {
  if (!previous) return 'fade';
  const curr = getDepth(current);
  const prev = getDepth(previous);
  if (curr > prev) return 'forward';
  if (curr < prev) return 'back';
  return 'fade';
}


// ─── Animated Routes ──────────────────────────────────────────────────────────
function AnimatedRoutes() {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);
  useDisableSwipeBack();

  const variant = getTransitionVariant(location.pathname, prevPath.current);
  prevPath.current = location.pathname;

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false} custom={variant}>
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/native-callback" element={<NativeCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public routes with layout */}
          <Route path="/" element={<AppLayout><Home /></AppLayout>} />
          <Route path="/catalogo" element={<AppLayout><Catalogo /></AppLayout>} />
          <Route path="/catalogo/categoria/:categorySlug" element={<AppLayout><Catalogo /></AppLayout>} />
          <Route path="/catalogo/categoria/:categorySlug/:subcategorySlug" element={<AppLayout><Catalogo /></AppLayout>} />
          <Route path="/catalogo/:id" element={<ProductDetail />} />
          <Route path="/promo" element={<AppLayout><Promo /></AppLayout>} />
          <Route path="/concierge" element={<AppLayout><Concierge /></AppLayout>} />
          <Route path="/info-negozio" element={<InfoNegozio />} />
          <Route path="/termini-condizioni" element={<TerminiCondizioni />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/notifiche" element={
            <ProtectedRoute><Notifiche /></ProtectedRoute>
          } />
          <Route path="/wishlist" element={
            <ProtectedRoute><AppLayout><Wishlist /></AppLayout></ProtectedRoute>
          } />

          {/* Protected routes */}
          <Route path="/card" element={
            <ProtectedRoute><AppLayout><Card /></AppLayout></ProtectedRoute>
          } />
          <Route path="/ricompense" element={
            <ProtectedRoute><AppLayout><Ricompense /></AppLayout></ProtectedRoute>
          } />
          <Route path="/ricompense/:id" element={
            <ProtectedRoute><RewardDetail /></ProtectedRoute>
          } />
          <Route path="/profilo" element={
            <ProtectedRoute><Profilo /></ProtectedRoute>
          } />
          <Route path="/miei-riscatti" element={
            <ProtectedRoute><AppLayout><MieiRiscatti /></AppLayout></ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin><Admin /></ProtectedRoute>
          } />
          <Route path="/admin/gestione-admin" element={
            <ProtectedRoute requireAdmin><AdminGestioneAdmin /></ProtectedRoute>
          } />
          <Route path="/admin/carica-punti/:userId" element={
            <ProtectedRoute requireAdmin><AdminCaricaPunti /></ProtectedRoute>
          } />
          <Route path="/admin/scaglioni/:tierId" element={
            <ProtectedRoute requireAdmin><AdminScaglioneDetail /></ProtectedRoute>
          } />
          <Route path="/admin/movimenti/:transactionId" element={
            <ProtectedRoute requireAdmin><AdminMovimentoDetail /></ProtectedRoute>
          } />
          <Route path="/admin/prodotti/nuovo" element={
            <ProtectedRoute requireAdmin><AdminNuovoProdotto /></ProtectedRoute>
          } />
          <Route path="/admin/prodotti/:productId" element={
            <ProtectedRoute requireAdmin><AdminNuovoProdotto /></ProtectedRoute>
          } />
          <Route path="/admin/riscatti/:redemptionId" element={
            <ProtectedRoute requireAdmin><AdminRedemptionDetail /></ProtectedRoute>
          } />
          <Route path="/admin/categorie/:categoryId" element={
            <ProtectedRoute requireAdmin><AdminCategoryDetail /></ProtectedRoute>
          } />
          <Route path="/admin/promo/:promoId" element={
            <ProtectedRoute requireAdmin><AdminPromoDetail /></ProtectedRoute>
          } />
          <Route path="/admin/promo-archivio" element={
            <ProtectedRoute requireAdmin><AdminPromoArchivio /></ProtectedRoute>
          } />
          <Route path="/admin/ricompense/:rewardId" element={
            <ProtectedRoute requireAdmin><AdminRewardDetail /></ProtectedRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <DesktopGate>
      {showSplash && (
        <SplashScreen
          onComplete={() => setShowSplash(false)}
          minDuration={1000}
        />
      )}
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <Sonner />
              <ShadcnToaster />
              <ScrollToTop />
              <AnimatedRoutes />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </DesktopGate>
  );
};

export default App;
